import React, { useState, useEffect } from 'react';
import { X, Clock, User, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const RemindsHistory = ({ remindId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [remindId]);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`http://localhost:3001/api/reminds/${remindId}/history`);
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es });
    } catch (error) {
      return date;
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'created':
        return 'Creado';
      case 'updated':
        return 'Actualizado';
      case 'completed':
        return 'Completado';
      case 'status_changed':
        return 'Estado cambiado';
      default:
        return action;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created':
        return 'bg-green-100 text-green-800';
      case 'updated':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'status_changed':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Historial de Cambios</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando historial...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-gray-600">No hay historial disponible</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, index) => (
                <div
                  key={entry.id || index}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getActionColor(entry.action)}`}>
                      {getActionLabel(entry.action)}
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock size={14} className="mr-1" />
                      {formatDate(entry.changed_at)}
                    </div>
                  </div>

                  {entry.changed_by && (
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <User size={14} className="mr-2" />
                      <span className="font-medium">{entry.changed_by}</span>
                    </div>
                  )}

                  {entry.changes && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Cambios realizados:</p>
                      <div className="bg-white rounded p-3 text-sm">
                        <pre className="whitespace-pre-wrap text-gray-600 font-mono text-xs">
                          {typeof entry.changes === 'string' 
                            ? entry.changes 
                            : JSON.stringify(entry.changes, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemindsHistory;
